<script setup lang="ts">
/*
  Where a new project or dataspace goes: "Personal", or one of the departments the caller may
  create there.

  The create dialogs own what is allowed (it depends on the entitlement they create under) and hand
  it in as `allowed`; this draws the choice. It is not drawn at all once it is known there is
  nothing to choose — a caller in no department that lets them create has only personal — and
  while that is still being asked it is a disabled select saying so, rather than a field that
  appears under the user's hands.

  The value is a department's id, or `PERSONAL` (`utils/spaceLocation.ts`) — never `""`, which a
  select reads as nothing chosen.

  "Personal" is left out when the server's license allows no personal spaces (`personal` off, from
  the dialog's `personalSpacesAllowed`), and the one line under the field says why: a space made
  personal there would be locked at once. A caller left with no department to pick has nothing to
  choose, and the dialog says so.
*/
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { mdiAccountOutline, mdiOfficeBuildingOutline } from "@mdi/js";
import { HabSelect, type HabSelectOption } from "@livereader/habitus-vue";
import { PERSONAL } from "@/shared/utils/spaceLocation";

const props = withDefaults(
	defineProps<{
		/** The departments the caller may create in. */
		allowed: readonly { id: string; name: string }[];
		/** Still asking what the caller may do in their departments. */
		loading?: boolean;
		/** Personal spaces are allowed: the license has them on. Off, "Personal" is not offered. */
		personal?: boolean;
		label?: string;
		help?: string;
	}>(),
	{ personal: true, label: undefined, help: undefined },
);

const model = defineModel<string>({ required: true });

const { t } = useI18n();

const offerPersonal = computed(() => props.personal);

const options = computed<HabSelectOption[]>(() => [
	...(offerPersonal.value
		? [{ value: PERSONAL, label: t("spaces.personal"), icon: mdiAccountOutline }]
		: []),
	...props.allowed.map((d) => ({ value: d.id, label: d.name, icon: mdiOfficeBuildingOutline })),
]);
</script>

<template>
	<HabSelect
		v-if="loading || allowed.length"
		v-model="model"
		:options="options"
		:label="label ?? $t('spaces.picker.label')"
		:help="
			help ??
			(offerPersonal ? $t('spaces.picker.help') : $t('spaceRestriction.picker.personalOff'))
		"
		:state="loading ? 'disabled' : 'default'"
		:placeholder="loading ? t('common.status.loading') : undefined"
	/>
</template>
